import { reactive, watch } from 'cdui-js';
import './style.css';
import { ActionSheetCheckIcon, showActionSheet } from '../ActionSheet';
import i18n from '../../../../i18n';
import { ButtonSelectContent } from './ButtonSelectContent';
import { busConfigs, busConfigsTick } from '../../../../stores/bus-config';

interface Option {
  label: any;
  value: string | number;
}

interface Props {
  title?: string;
  placeholder?: string;
  value: string | number;
  /** 静态选项；与 dict 二选一（dict 优先） */
  options?: Option[];
  /**
   * 业务字典 key（如 gender）。从 busConfigs 读取，并订阅 busConfigsTick，
   * 覆盖 App Theme / 接口晚到导致有 value 无 label 的问题。
   */
  dict?: string;
  change?: any;
  border?: boolean;
  style?: any;
  render?: (props: any) => any;
  multiple?: boolean;
  /** 弹层选项布局：list=列表（默认），button=按钮组 */
  optionLayout?: 'list' | 'button';
  disabled?: boolean;
}

// ── Multi-select panel (own reactive state, rendered inside ActionSheet) ──────
const MultiSelectContent = (panelProps: {
  title: string;
  options: Option[];
  initialValue: string;
  onConfirm: (val: string) => void;
  close: () => void;
}) => {
  const toArr = (v: string): string[] =>
    v
      ? v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const state = reactive({ selected: toArr(panelProps.initialValue) });

  const toggle = (val: string) => {
    const idx = state.selected.indexOf(val);
    if (idx === -1) {
      state.selected = [...state.selected, val];
    } else {
      state.selected = state.selected.filter((v) => v !== val);
    }
  };

  const isChecked = (val: string) => state.selected.includes(val);

  return (
    <div>
      <div>
        {panelProps.options.map((o) => {
          const val = String(o.value);
          return (
            <div
              class={`action-sheet-option${isChecked(val) ? ' action-sheet-option-active' : ''}`}
              onclick={() => toggle(val)}
            >
              <div class="action-sheet-option-label">{typeof o.label === 'function' ? o.label() : o.label}</div>
              {isChecked(val) && <ActionSheetCheckIcon />}
            </div>
          );
        })}
      </div>

      <div class="f-select-multi-actions">
        <div class="f-select-multi-btn f-select-multi-btn-cancel" onclick={panelProps.close}>
          {i18n.Common.Cancel}
        </div>
        <div
          class="f-select-multi-btn f-select-multi-btn-confirm"
          onclick={() => {
            panelProps.onConfirm(state.selected.join(','));
            panelProps.close();
          }}
        >
          {i18n.Common.Confirm}
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export const SelectBox = (props: Props) => {
  const state = reactive({ dialog: null, dictRev: 0 });
  const border = props.border ?? true;

  /** App Theme / 接口灌入字典后刷新 options 与展示文案 */
  watch(
    () => busConfigsTick.v,
    (v) => {
      state.dictRev = v;
    },
  );

  const getOptions = (): Option[] => {
    void state.dictRev;
    if (props.dict) {
      const list = (busConfigs as Record<string, Option[]>)[props.dict];
      return Array.isArray(list) ? list : [];
    }
    return props.options || [];
  };

  const valueKey = (v: string | number | null | undefined) =>
    v === null || v === undefined || v === '' ? '' : String(v);

  const isSameValue = (a: string | number | null | undefined, b: string | number | null | undefined) =>
    valueKey(a) !== '' && valueKey(a) === valueKey(b);

  const openButtonSheet = () => {
    if (props.disabled) return;
    const options = getOptions();
    state.dialog = showActionSheet({
      title: props.title || '',
      content: () => (
        <ButtonSelectContent
          options={options}
          initialValue={valueKey(props.value)}
          multiple={!!props.multiple}
          onConfirm={(val) => props?.change(val)}
          close={() => state.dialog?.close()}
        />
      ),
    });
  };

  const handleClick = () => {
    if (props.disabled) return;
    if (props.optionLayout === 'button') {
      openButtonSheet();
      return;
    }

    const options = getOptions();

    if (props.multiple) {
      // ── Multi-select mode ──
      state.dialog = showActionSheet({
        title: props.title || '',
        content: () => (
          <MultiSelectContent
            title={props.title || ''}
            options={options}
            initialValue={valueKey(props.value)}
            onConfirm={(val) => props?.change(val)}
            close={() => state.dialog?.close()}
          />
        ),
      });
    } else {
      // ── Single-select mode (original logic) ──
      state.dialog = showActionSheet({
        title: props.title || '',
        content: () => (
          <div style={{ 'padding-bottom': '20px' }}>
            {!props.render
              ? options.map((o) => (
                  <div
                    class={`action-sheet-option${isSameValue(props.value, o.value) ? ' action-sheet-option-active' : ''}`}
                    onclick={() => {
                      if (isSameValue(o.value, props.value)) return;
                      props?.change(o.value);
                      state.dialog.close();
                    }}
                  >
                    <div class="action-sheet-option-label">
                      {typeof o.label === 'function' ? o.label?.() : o.label}
                    </div>
                    {isSameValue(props.value, o.value) && <ActionSheetCheckIcon />}
                  </div>
                ))
              : props.render({ options, dialog: state.dialog, close: () => state.dialog.close() })}
          </div>
        ),
      });
    }
  };

  // Display label(s) in the trigger
  const getDisplayText = () => {
    const options = getOptions();
    if (props.multiple) {
      const vals = valueKey(props.value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!vals.length) return null;
      const labels = vals.map((v) => options.find((o) => String(o.value) === v)?.label).filter(Boolean);
      return labels.join('、');
    }
    if (valueKey(props.value) === '') return null;
    return options.find((i) => isSameValue(i.value, props.value))?.label ?? null;
  };

  const isDisabled = () => !!props.disabled;

  return (
    <div
      class={`${border ? 'f-select-box' : 'f-select-box f-select-box__no-border'}${isDisabled() ? ' f-select-box-disabled' : ''}`.trim()}
      style={{ ...props.style }}
      aria-disabled={isDisabled() ? 'true' : undefined}
      tabindex={isDisabled() ? -1 : undefined}
      onclick={() => handleClick()}
    >
      {getDisplayText() ? (
        <div class="f-select-box-display">{getDisplayText()}</div>
      ) : (
        <div class="f-select-box-placeholder">{props.placeholder || i18n.Common.PleaseSelect}</div>
      )}

      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M9.12525 11.8998C9.51442 12.2827 10.1388 12.2827 10.528 11.8998L14.2899 8.19838C14.8001 7.69631 14.4446 6.82813 13.7288 6.82813H5.92444C5.20858 6.82813 4.85308 7.69631 5.36335 8.19838L9.12525 11.8998Z"
          fill="#A2A7AD"
        />
      </svg>
    </div>
  );
};
